import {
  PublicClientApplication,
  createNestablePublicClientApplication,
  EventType,
  type AuthenticationResult,
  type IPublicClientApplication,
} from '@azure/msal-browser'
import { msalConfig } from '@/lib/msalConfig'
import type { HostKind } from '@/lib/teamsHost'

/**
 * Web keeps the exact, proven `new PublicClientApplication()` construction
 * path unchanged. Teams uses `createNestablePublicClientApplication`, which
 * enables silent SSO through Teams' nested-app-auth bridge with zero
 * redirects when teams-js has already completed its handshake (main.tsx
 * calls detectHost() — and therefore teams-js's app.initialize() — before
 * this). The two paths are kept explicitly separate rather than always
 * using the "nestable" factory: its Teams-bridge probe
 * (NestedAppOperatingContext → BridgeProxy.create()) runs even when its own
 * fallback-to-standard branch is taken, and running that probe on every
 * plain web load caused a real, reproduced regression — `acquireTokenSilent`
 * timing out for Relaciones' API scope in production. Gating it behind the
 * already-known host avoids that path entirely for the (much more common)
 * web case.
 *
 * MSAL v5 REQUIRES `initialize()` to be awaited before ANY other API
 * (getAllAccounts, getActiveAccount, setActiveAccount, addEventCallback,
 * acquireTokenSilent, …). Calling them earlier throws
 * `uninitialized_public_client_application`, which crashes render to a blank
 * screen. This all runs once, before React mounts (main.tsx).
 */
export async function createMsalInstance(host: HostKind): Promise<IPublicClientApplication> {
  const instance =
    host === 'teams'
      ? await createNestablePublicClientApplication(msalConfig)
      : new PublicClientApplication(msalConfig)

  await instance.initialize()
  await instance.handleRedirectPromise()

  const cached = instance.getAllAccounts()
  if (cached.length > 0 && !instance.getActiveAccount()) {
    instance.setActiveAccount(cached[0])
  }

  instance.addEventCallback((event) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS ||
        // ssoSilent (the Teams path) fires ACQUIRE_TOKEN_SUCCESS, not a
        // dedicated event — there is no separate SSO-specific event type.
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
      (event.payload as AuthenticationResult)?.account
    ) {
      const account = (event.payload as AuthenticationResult).account
      instance.setActiveAccount(account)
      console.log('[SCC][MSAL] active account set from', event.eventType, '→', account?.username)
    }
  })

  return instance
}
